import type { ParsedUrlQuery } from "node:querystring";
import { useEffect, useMemo, useRef } from "react";
import Router from "next/router";
import { useGenericActivityFromElysiumMutation } from "src/api/client/generic-activity-from-elysium.api";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { IExtendedSearchData } from "src/api/server/extended-search.types";
import { ISelectedRanking } from "src/components/pages/search/components/types";
import { SearchQueryParams } from "src/components/pages/search/search.types";
import { setDefaultFilters, setNewSearchStoreValues } from "src/components/pages/search/store/actions";
import {
    ViewMode,
    TViewMode,
    getSearchState,
    ICommitSearchStateOptions,
    ISearchStore,
    registerOnUserCommit,
    resetSearchStore,
    setSearchStateSilently,
} from "src/components/pages/search/store/search-store";
import { loadViewMode, saveViewMode } from "src/components/pages/search/store/search-view-local-storage";
import { debounceSearchCommit } from "src/components/pages/search/utils/debounce-search-commit";
import { fixSearchUrlQuery } from "src/components/pages/search/utils/fix-search-url-query";
import { mapIsAtInvestableToQuery, mapIsChInvestableToQuery, mapIsInvestableToQuery } from "src/components/pages/search/utils/map-is-investable";
import { mapQueryStringToState } from "src/components/pages/search/utils/map-query-string-to-state";
import { mapSearchTermToQuery } from "src/components/pages/search/utils/map-search-term";
import { mapSelectedRankingToQuery } from "src/components/pages/search/utils/map-selected-ranking";
import { mapSortDirectionToQuery } from "src/components/pages/search/utils/map-sort-direction";
import { mapStateToQueryString } from "src/components/pages/search/utils/map-state-to-query-string";
import { mapViewFromRouterQuery, mapViewToQuery } from "src/components/pages/search/utils/map-view";
import { hasFilterParams, isOnlySearchTermPresent } from "src/components/pages/search/utils/search-query-predicates";
import { getNormalizedRouterQuery, urlSearchParamsToQueryString } from "src/components/pages/search/utils/url-params-helpers";
import { ICountry } from "src/constants/locale";
import { EnumCountryKey } from "src/types/common-enum";
import { useIsomorphicLayoutEffect } from "src/utils/common-util";
import { getRouterLocale } from "src/utils/router/use-router-locale";
import { sanitizeSearchTerm } from "src/utils/sanitize-search-term";

interface IUseSearchUrlSync {
    data: IExtendedSearchData;
    ranking: ISelectedRanking;
}

const getOriginPathName = () => {
    const { origin, pathname } = window.location;
    return `${origin}${pathname}?`;
};

const getInvestableQueryParts = (country: ICountry) => ({
    investableQuery: mapIsInvestableToQuery(country !== EnumCountryKey.INT),
    atInvestableQuery: country === EnumCountryKey.AT ? mapIsAtInvestableToQuery(true, country) : "",
    chInvestableQuery: country === EnumCountryKey.CH ? mapIsChInvestableToQuery(true, country) : "",
});

const buildDefaultQueryString = (
    data: IExtendedSearchData,
    ranking: ISelectedRanking,
    country: ICountry,
    view: TViewMode,
    searchTerm?: string
): string => {
    const { investableQuery, atInvestableQuery, chInvestableQuery } = getInvestableQueryParts(country);
    const searchTermParam = searchTerm ? mapSearchTermToQuery(sanitizeSearchTerm(searchTerm)) : "";
    const rankingSorted = searchTerm
        ? ""
        : `${mapSortDirectionToQuery(ranking.sortDirection)}&${mapSelectedRankingToQuery(ranking, data.rankingsData)}`;

    return [searchTermParam, investableQuery, atInvestableQuery, chInvestableQuery, rankingSorted, mapViewToQuery(view)].filter(Boolean).join("&");
};

const useUserCommitUrlWriter = (data: IExtendedSearchData) => {
    const genericActivity = useGenericActivityFromElysiumMutation();

    const latest = useRef({ data, track: genericActivity });

    useEffect(() => {
        latest.current = { data, track: genericActivity };
    }, [data, genericActivity]);

    const writeUrlDebounced = useMemo(
        () =>
            debounceSearchCommit((nextState: ISearchStore, options?: ICommitSearchStateOptions) => {
                const { data: currentData, track } = latest.current;
                const { country } = getRouterLocale();
                const { origin, pathname } = window.location;
                const caller = new URLSearchParams(window.location.search).get(SearchQueryParams.Caller);
                const queryString = mapStateToQueryString(nextState, currentData, country, caller);

                Router.replace({ pathname, query: queryString }, undefined, { shallow: true });

                if (options?.trackVisit === false) {
                    return;
                }

                track({
                    activityTypeId: EnumActivityType.VisitWikifolioSearchPage,
                    parameter: queryString,
                    referrer: `${origin}${pathname}?${queryString}`,
                });
            }),
        []
    );

    useEffect(() => {
        const unregister = registerOnUserCommit(writeUrlDebounced);
        return () => {
            unregister();
            writeUrlDebounced.cancel();
        };
    }, [writeUrlDebounced]);
};

const seedDefaultSelectedFigures = (data: IExtendedSearchData) => {
    const defaultFigureIds = (data.figuresPicker?.defaultFigures ?? []).map(figure => figure.id);
    setSearchStateSilently(current => ({ ...current, selectedFigures: defaultFigureIds }));
};

const applyInitialViewMode = (query: ParsedUrlQuery) => {
    const view = mapViewFromRouterQuery(getNormalizedRouterQuery(query)) ?? loadViewMode();
    if (view) {
        setSearchStateSilently(current => ({ ...current, view }));
    }
};

interface IHydrateFiltersFromUrl {
    data: IExtendedSearchData;
    country: ICountry;
    ranking: ISelectedRanking;
    trackVisit: (parameter: string) => void;
}

const hydrateFiltersFromUrl = ({ data, country, ranking, trackVisit }: IHydrateFiltersFromUrl) => {
    const { query } = Router;
    const { searchTerm } = getNormalizedRouterQuery(query);
    const sanitizedSearchTerm = searchTerm ? sanitizeSearchTerm(searchTerm as string) : undefined;

    const replaceUrl = (queryString: string) => Router.replace(`${getOriginPathName()}${queryString}`, undefined, { shallow: true });

    if (!hasFilterParams(query)) {
        const defaultQuery = buildDefaultQueryString(data, ranking, country, getSearchState().view);
        const defaultFigureIds = (data.figuresPicker?.defaultFigures ?? []).map(figure => figure.id);
        setSearchStateSilently(current => ({
            ...current,
            selectedFigures: defaultFigureIds,
        }));
        setDefaultFilters(ranking, country);
        replaceUrl(defaultQuery);
        trackVisit(defaultQuery);
        return;
    }

    const fixedUrlSearchParams = fixSearchUrlQuery({ query, country, extendedSearchData: data });
    const newState = mapQueryStringToState(fixedUrlSearchParams, data);

    if (isOnlySearchTermPresent(query)) {
        const { investableQuery, atInvestableQuery, chInvestableQuery } = getInvestableQueryParts(country);
        const defaultQuery = buildDefaultQueryString(data, ranking, country, newState.view, sanitizedSearchTerm);
        replaceUrl(defaultQuery);
        setNewSearchStoreValues({
            ...newState,
            isInvestable: Boolean(investableQuery),
            isAtInvestable: Boolean(atInvestableQuery),
            isChInvestable: Boolean(chInvestableQuery),
            query: sanitizedSearchTerm ?? "",
            sortDirection: null,
            selectedRanking: { id: null, sortDirection: null },
        });
        trackVisit(defaultQuery);
        return;
    }

    const { pathname } = window.location;
    if (newState.view === ViewMode.Table) {
        fixedUrlSearchParams.set(SearchQueryParams.View, newState.view);
    } else {
        fixedUrlSearchParams.delete(SearchQueryParams.View);
    }
    const fixedQueryParams = urlSearchParamsToQueryString(fixedUrlSearchParams);

    Router.replace({ pathname, query: fixedQueryParams }, undefined, { shallow: true });
    setNewSearchStoreValues(newState);
    trackVisit(fixedQueryParams);
};

const useHydrateFromUrl = (data: IExtendedSearchData, ranking: ISelectedRanking) => {
    const genericActivity = useGenericActivityFromElysiumMutation();

    useIsomorphicLayoutEffect(() => {
        const { country } = getRouterLocale();

        const trackVisit = (parameter: string) =>
            genericActivity({
                activityTypeId: EnumActivityType.VisitWikifolioSearchPage,
                parameter,
                referrer: `${getOriginPathName()}${parameter}`,
            });

        seedDefaultSelectedFigures(data);
        applyInitialViewMode(Router.query);
        hydrateFiltersFromUrl({ data, country, ranking, trackVisit });
        setSearchStateSilently(current => ({ ...current, hasHydratedFromUrl: true }));
        saveViewMode(getSearchState().view);

        return () => {
            resetSearchStore();
        };
    }, [data]);
};

export const useSearchUrlSync = ({ data, ranking }: IUseSearchUrlSync) => {
    useUserCommitUrlWriter(data);
    useHydrateFromUrl(data, ranking);
};
